import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import './PaymentSuccess.scss';
import { handleCheckoutSuccess } from '../services/stripeService';
import { FaCheck, FaBoxOpen, FaEnvelope } from 'react-icons/fa';

const PaymentSuccess = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    async function processPayment() {
      try {
        // Parse the query parameters to get session ID and order ID
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        const orderId = params.get('order_id');
        
        if (!sessionId || !orderId) {
          throw new Error('Missing required payment information');
        }
        
        // Fetch the order details
        const orderRef = doc(db, 'orders', orderId);
        const orderSnap = await getDoc(orderRef);
        
        if (!orderSnap.exists()) {
          throw new Error('Order not found');
        }
        
        const orderData = orderSnap.data();
        
        // Fetch the user details
        const userRef = doc(db, 'fredoRegisteredUsers', orderData.userId);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          throw new Error('User details not found');
        }
        
        const userData = userSnap.data();
        
        // Process the successful payment
        const result = await handleCheckoutSuccess(sessionId, orderId, userData);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to process payment confirmation');
        }
        
        // Combine order and user data for display
        setOrderDetails({
          ...orderData,
          user: userData,
          orderId
        });
        
        // Store receipt URL if available
        if (result.receiptUrl) {
          setReceipt(result.receiptUrl);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error processing payment success:', error);
        setError(error.message || 'An error occurred while processing your payment');
        setLoading(false);
      }
    }
    
    processPayment();
  }, [location]);

  if (loading) {
    return (
      <div className="payment-success-page">
        <div className="success-container loading">
          <div className="spinner"></div>
          <h2>Processing your payment...</h2>
          <p>Please wait while we confirm your order.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-page">
        <div className="success-container error">
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <Link to="/" className="btn-primary">Return to Homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-page">
      <div className="success-container">
        <div className="success-icon">
          <FaCheck />
        </div>
        
        <h1>Payment Successful!</h1>
        <p className="success-message">
          Thank you for your purchase. Your order has been received and is being processed.
        </p>
        
        {orderDetails && (
          <div className="order-details">
            <h2>Order Information</h2>
            
            <div className="detail-row">
              <span>Order ID:</span>
              <span>{orderDetails.orderId}</span>
            </div>
            
            <div className="detail-row">
              <span>Package:</span>
              <span>{orderDetails.packageTitle}</span>
            </div>
            
            <div className="detail-row">
              <span>Quantity:</span>
              <span>{orderDetails.quantity}</span>
            </div>
            
            <div className="detail-row">
              <span>Total Amount:</span>
              <span className="amount">${orderDetails.totalPrice}</span>
            </div>
            
            <div className="detail-row">
              <span>Order Date:</span>
              <span>{new Date(orderDetails.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        )}
        
        <div className="next-steps">
          <h2>What&apos;s Next?</h2>
          
          <div className="step-item">
            <FaEnvelope className="step-icon" />
            <div className="step-content">
              <h3>Check Your Email</h3>
              <p>We&apos;ve sent a receipt and order confirmation to your email address.</p>
            </div>
          </div>
          
          <div className="step-item">
            <FaBoxOpen className="step-icon" />
            <div className="step-content">
              <h3>Our Team Will Contact You</h3>
              <p>A member of our team will reach out to you within 24 hours to discuss the next steps for your project.</p>
            </div>
          </div>
        </div>
        
        {receipt && (
          <a 
            href={receipt} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-secondary"
          >
            View Receipt
          </a>
        )}
        
        <Link to="/" className="btn-primary">Return to Homepage</Link>
      </div>
    </div>
  );
};

export default PaymentSuccess; 